import {useState, useEffect, useCallback} from "react"
import {Button, Typography} from "@mui/material"
import {paletteColors} from "@indebted/theme"
import {useTranslation} from "react-i18next"
import {colors} from "@indebted/theme/colors"

export function JumpToBottomButton({
	hasStickyButton,
	bottomTolerance,
}: {
	hasStickyButton: boolean
	bottomTolerance: number
}) {
	const [isVisible, setIsVisible] = useState(false)
	const {t} = useTranslation()

	const handleScroll = useCallback(() => {
		const scrollTop = window.scrollY || document.documentElement.scrollTop
		const scrollHeight = document.documentElement.scrollHeight
		const clientHeight = document.documentElement.clientHeight

		// Show button when not at the bottom (with some tolerance)
		const isAtBottom = scrollTop + clientHeight >= scrollHeight - bottomTolerance
		setIsVisible(!isAtBottom)
	}, [bottomTolerance])

	useEffect(() => {
		window.addEventListener("scroll", handleScroll)
		handleScroll()
		return () => {
			window.removeEventListener("scroll", handleScroll)
		}
	}, [handleScroll])

	const scrollToBottom = () => {
		window.scrollTo({
			top: document.documentElement.scrollHeight,
			behavior: "smooth",
		})
		setIsVisible(false)
	}

	if (!isVisible) return null

	return (
		<Button
			sx={{
				position: "fixed",
				bottom: `${hasStickyButton ? 108 : 24}px`,
				left: "50%",
				transform: "translateX(-50%)",
				cursor: "pointer",
				transition: "opacity 0.3s ease",
				padding: "10px",
				gap: "8px",
				backgroundColor: colors.primary.white,
				borderRadius: 0.5, // 4px
				boxShadow: "0px 4px 24px 0px rgba(54, 59, 100, 0.40)",
				zIndex: 1000,
				textTransform: "none",
				"&:hover": {
					backgroundColor: colors.primary.white,
				},
			}}
			onClick={scrollToBottom}
		>
			<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
				<path
					d="M4 6L8 10L12 6"
					stroke={paletteColors.indebtedPrimary}
					strokeLinecap="round"
					strokeLinejoin="round"
				/>
			</svg>
			<Typography
				variant="body2"
				color="primary"
				sx={{
					fontWeight: "bold",
					fontSize: "14px",
					lineHeight: "20px",
					color: paletteColors.indebtedPrimary,
					whiteSpace: "nowrap",
				}}
			>
				{t("payment_arrangement_efta.jump_to_bottom")}
			</Typography>
		</Button>
	)
}
